import { EmptyState, Flex, InfoBox, LoadingIndicator } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { useGetDdqStructureQuery } from "~/graphql/supergraphHooks";
import { buildTree } from "./buildTree";
import { SectionNode } from "./SectionNode";
import * as styles from "./styles_vanilla.css";

export type QuestionTreeProps = {
  arenaId: number;
  ddqId: string;
};

export const QuestionTree = ({ arenaId, ddqId }: QuestionTreeProps) => {
  const { data, loading, error } = useGetDdqStructureQuery({
    context: withClientContext("federation"),
    variables: { arenaId, ddqId },
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  if (loading && !data) {
    return (
      <Flex
        justifyContent="center"
        className={styles.loadingState}
        data-testid="ddq-question-tree-loading"
      >
        <LoadingIndicator size="large" />
      </Flex>
    );
  }

  // errorPolicy "all" can hand back an error alongside data; only a total failure has none to show.
  if (error && !data) {
    return (
      <Flex className={styles.errorState} data-testid="ddq-question-tree-error">
        <InfoBox variant="error" title="Couldn't load the question structure." />
      </Flex>
    );
  }

  const sections = buildTree(data?.ddqSections ?? [], data?.ddqQuestions ?? []);

  if (sections.length === 0) {
    return (
      <EmptyState
        title="No structure yet"
        className={styles.emptyState}
        data-testid="ddq-question-tree-empty"
      />
    );
  }

  return (
    <Flex flexDirection="column" className={styles.tree} data-testid="ddq-question-tree">
      {sections.map((section) => (
        <SectionNode key={section.id} section={section} arenaId={arenaId} ddqId={ddqId} />
      ))}
    </Flex>
  );
};
